import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2, AfterViewInit, Output, EventEmitter } from '@angular/core';

@Directive({
  selector: '[appLazyLoadImage]'
})
export class LazyLoadImageDirective implements OnInit, OnDestroy, AfterViewInit {
  @Input() appLazyLoadImage?: string; // The image URL to lazy load
  @Input() lazyLoadPlaceholder?: string; // Optional placeholder image
  @Input() lazyLoadThreshold = 100; // Distance from viewport to start loading (in pixels)
  @Output() imageLoaded = new EventEmitter<void>();
  @Output() imageError = new EventEmitter<void>();

  private intersectionObserver?: IntersectionObserver;
  private hasLoaded = false;
  private readonly imageElement?: HTMLImageElement;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2
  ) {}

  ngOnInit(): void {
    // Set placeholder if provided
    if (this.lazyLoadPlaceholder) {
      this.setImageSrc(this.lazyLoadPlaceholder);
    }
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  ngOnDestroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  private setupIntersectionObserver(): void {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: load image immediately if IntersectionObserver is not supported
      this.loadImage();
      return;
    }

    const options: IntersectionObserverInit = {
      root: null, // Use viewport as root
      rootMargin: `${this.lazyLoadThreshold}px`, // Start loading before element enters viewport
      threshold: 0.01 // Trigger when at least 1% of the element is visible
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasLoaded) {
          this.loadImage();
        }
      });
    }, options);

    // Start observing the element
    this.intersectionObserver.observe(this.elementRef.nativeElement as Element);
  }

  private loadImage(): void {
    if (!this.appLazyLoadImage || this.hasLoaded) {
      return;
    }

    this.hasLoaded = true;

    // Get the image element (could be img tag or element with background-image)
    const element = this.elementRef.nativeElement;

    if (element.tagName === 'IMG') {
      this.loadImageElement(element as HTMLImageElement);
    } else {
      // For div or other elements, set as background image
      this.loadBackgroundImage(element as HTMLElement);
    }

    // Stop observing once loaded
    if (this.intersectionObserver) {
      this.intersectionObserver.unobserve(this.elementRef.nativeElement as Element);
    }
  }

  private loadImageElement(imgElement: HTMLImageElement): void {
    // Create a new image to preload
    const tempImage = new Image();

    tempImage.onload = () => {
      // Once loaded, update the actual image src
      this.renderer.setAttribute(imgElement, 'src', this.appLazyLoadImage ?? '');
      this.renderer.addClass(imgElement, 'lazy-loaded');

      // Add fade-in animation
      this.renderer.setStyle(imgElement, 'animation', 'fadeIn 0.3s ease-in');

      // Emit loaded event
      this.imageLoaded.emit();
    };

    tempImage.onerror = () => {
      // If loading fails, add error class for styling
      this.renderer.addClass(imgElement, 'lazy-load-error');

      // Optionally set a fallback image
      if (this.lazyLoadPlaceholder) {
        this.renderer.setAttribute(imgElement, 'src', this.lazyLoadPlaceholder);
      }

      // Emit error event
      this.imageError.emit();
    };

    // Start loading the image
    tempImage.src = this.appLazyLoadImage ?? '';
  }

  private loadBackgroundImage(element: HTMLElement): void {
    // Create a new image to preload
    const tempImage = new Image();

    tempImage.onload = () => {
      // Once loaded, update the background image
      this.renderer.setStyle(element, 'background-image', `url(${this.appLazyLoadImage})`);
      this.renderer.addClass(element, 'lazy-loaded');

      // Add fade-in animation
      this.renderer.setStyle(element, 'animation', 'fadeIn 0.3s ease-in');

      // Emit loaded event
      this.imageLoaded.emit();
    };

    tempImage.onerror = () => {
      // If loading fails, add error class for styling
      this.renderer.addClass(element, 'lazy-load-error');

      // Optionally set a fallback image
      if (this.lazyLoadPlaceholder) {
        this.renderer.setStyle(element, 'background-image', `url(${this.lazyLoadPlaceholder})`);
      }

      // Emit error event
      this.imageError.emit();
    };

    // Start loading the image
    tempImage.src = this.appLazyLoadImage ?? '';
  }

  private setImageSrc(src: string): void {
    const element = this.elementRef.nativeElement;

    if (element.tagName === 'IMG') {
      this.renderer.setAttribute(element, 'src', src);
    } else {
      this.renderer.setStyle(element, 'background-image', `url(${src})`);
    }
  }
}

